import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Paper,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import { setActiveCurrency } from "../../utils/currency";

export default function SelectTenant() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, tenants, isLoading, error } = useSelector((state) => state.auth);
  const [switchingId, setSwitchingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [revokedMsg, setRevokedMsg] = useState(() => {
    const msg = sessionStorage.getItem('tenantAccessRevokedMessage');
    if (msg) {
      sessionStorage.removeItem('tenantAccessRevokedMessage');
    }
    return msg || "";
  });

  useEffect(() => {
    const roleName = user?.role?.name || user?.role || "";
    const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
    if (isSuperAdmin && !revokedMsg) {
      navigate("/users");
      return;
    }
  }, [user, navigate, revokedMsg]);

  const filteredTenants = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return tenants;
    return tenants.filter((t) => {
      const name = (t.name || t.hotelName || "").toLowerCase();
      const location = (t.location || "").toLowerCase();
      return name.includes(query) || location.includes(query);
    });
  }, [tenants, searchQuery]);

  const handleSelect = async (tenant) => {
    const propertyUuid = tenant.id || tenant._id;
    const stringTenantId = tenant.tenantId || propertyUuid;
    setSwitchingId(propertyUuid);
    try {
      if (tenant?.currency) {
        setActiveCurrency(tenant.currency);
      }
      const result = await dispatch.auth.switchTenantAsync({
        payloadTenantId: propertyUuid,
        stringTenantId: stringTenantId,
      });
      if (result.success) {
        window.location.href = "/dashboard";
      }
    } catch (err) {
      console.error("Failed to select tenant:", err);
    } finally {
      setSwitchingId(null);
    }
  };

  useEffect(() => {
    dispatch.auth.fetchTenantsAsync().then((res) => {
      const roleName = user?.role?.name || user?.role || "";
      const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
      if (!isSuperAdmin && res?.success && res?.tenants) {
        // If a revocation message exists, we don't auto-select the remaining tenant 
        // so the user actually has a chance to read the message.
        if (!revokedMsg && res.tenants.length === 1) {
          handleSelect(res.tenants[0]);
        }
      }
    });
  }, [dispatch, user, revokedMsg]);

  const handleLogout = async () => {
    await dispatch.auth.logoutAsync();
    navigate("/login");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a0f1d 0%, #1e1b4b 100%)",
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            background: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: 4,
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
            overflow: "visible",
            position: "relative",
          }}
        >
          {/* Logout button top-right */}
          <Button
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "rgba(255, 255, 255, 0.6)",
              "&:hover": {
                color: "#ff4d4d",
                background: "rgba(255, 77, 77, 0.1)",
              },
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Logout
          </Button>

          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Box sx={{ textCenter: "center", mb: 4, mt: 2, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "primary.main",
                  margin: "0 auto 16px",
                  boxShadow: "0 0 20px rgba(25, 118, 210, 0.5)",
                }}
              >
                <BusinessIcon sx={{ fontSize: 32, color: "#fff" }} />
              </Avatar>
              <Typography
                variant="h4"
                fontWeight="800"
                sx={{
                  color: "#fff",
                  letterSpacing: "-0.5px",
                  mb: 1,
                  background: "linear-gradient(90deg, #fff 0%, #a5b4fc 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Select Property
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.5)", fontWeight: 500 }}>
                Please choose a tenant context to manage
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {revokedMsg && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                {revokedMsg}
              </Alert>
            )}

            {tenants.length > 0 && (
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    borderRadius: 3,
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    "& fieldset": {
                      borderColor: "transparent",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {isLoading && tenants.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
                <CircularProgress size={40} thickness={4} sx={{ color: "primary.main" }} />
              </Box>
            ) : tenants.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.6)", mb: 2 }}>
                  No accessible properties found for your account.
                </Typography>
              </Box>
            ) : filteredTenants.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.5)", mb: 2 }}>
                  No properties match your search.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredTenants.map((tenant) => {
                  const tenantIdVal = tenant.id || tenant._id;
                  const isCurrentSwitching = switchingId === tenantIdVal;
                  return (
                    <Grid item xs={12} key={tenantIdVal}>
                      <Paper
                        onClick={() => !switchingId && handleSelect(tenant)}
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          cursor: switchingId ? "default" : "pointer",
                          background: "rgba(255, 255, 255, 0.02)",
                          border: "1px solid rgba(255, 255, 255, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          "&:hover": {
                            transform: switchingId ? "none" : "translateY(-3px)",
                            background: "rgba(255, 255, 255, 0.08)",
                            borderColor: "primary.main",
                            boxShadow: "0 8px 24px rgba(25, 118, 210, 0.15)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: "rgba(255, 255, 255, 0.05)",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              color: "primary.light",
                            }}
                          >
                            {(tenant.name || tenant.hotelName || "P")[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "#fff" }}>
                              {tenant.name || tenant.hotelName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.4)" }}>
                              {tenant.location || "PMSGO Properties"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          {isCurrentSwitching ? (
                            <CircularProgress size={20} thickness={5} sx={{ color: "primary.light" }} />
                          ) : (
                            <ArrowForwardIcon
                              sx={{
                                color: "rgba(255, 255, 255, 0.3)",
                                transition: "all 0.2s",
                                ".MuiPaper-root:hover &": {
                                  color: "primary.light",
                                  transform: "translateX(3px)",
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
